import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { UserActivity } from '../../../classes/models/user/UserActivity';
import moment from 'moment';
import { KeyValue } from '@angular/common';
import { StoreSummary } from '../../../classes/models/store/StoreSummary';
import { DataService } from '../../../services/store/data.service';
import { UserActivityService } from '../../../services/http/user/user-activity.service';
import { SHARED_IMPORTS } from '../../../shared/standalone-imports';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * This component displays user activity statistics.
 */
@Component({
    selector: 'app-user-activity',
    templateUrl: './user-activity.component.html',
    styleUrls: ['./user-activity.component.less'],
    imports: [SHARED_IMPORTS]
})
export class UserActivityComponent implements OnInit {
  private userActivityService = inject(UserActivityService);
  private dataService = inject(DataService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** A list daily user activity. */
  userActivities: UserActivity[] = [];
  /** Aggregated user activity data from month to user count. */
  monthToUserCountMap: Map<string, number>;
  /** Aggregated user activity data from week to user count. */
  weekToUserCountMap: Map<string, number>;
  /** Aggregated user activity data from week to daily user activity. */
  weekToDailyUserCountMap: Map<string, Map<string, number>>;
  /** The selected store. */
  selectedStore: StoreSummary;

  /** Week format, used to format keys of {@link weekToUserCountMap}. */
  WEEK_FORMAT = 'GGGG-WW';
  /** Month format, used to format keys of {@link monthToUserCountMap}. */
  MONTH_FORMAT = 'YYYY-MM';

  /**
   * Retrieves user activity from backend and groups it by month, week and day.
   */
  ngOnInit(): void {
    this.userActivityService.getUserActivity()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: userActivities => {
          this.userActivities = userActivities;
          this.groupUserActivities(userActivities);
        }
      });
    this.selectedStore = this.dataService.selectedStore;
  }

  private groupUserActivities(userActivities: UserActivity[]): void {
    const monthlyActivities = this.groupByKey(userActivities, this.MONTH_FORMAT);
    this.monthToUserCountMap = this.toUserCountMap(monthlyActivities);

    const weeklyActivities = this.groupByKey(userActivities, this.WEEK_FORMAT);
    this.weekToUserCountMap = this.toUserCountMap(weeklyActivities);

    const weekToDailyUserCountMap = new Map<string, Map<string, number>>();
    weeklyActivities.forEach((activities, week) => {
      weekToDailyUserCountMap.set(week, this.toDailyUserCountMap(activities));
    });
    this.weekToDailyUserCountMap = weekToDailyUserCountMap;
  }

  private groupByKey(userActivities: UserActivity[], dateFormat: string): Map<string, UserActivity[]> {
    const groupedActivities = new Map<string, UserActivity[]>();
    userActivities.forEach(activity => {
      const formattedDate = moment(activity.date).format(dateFormat);
      const previousActivities = groupedActivities.get(formattedDate) ? groupedActivities.get(formattedDate) : [];
      groupedActivities.set(formattedDate, [...previousActivities, activity]);
    });
    return groupedActivities;
  }

  private toUserCountMap(groupedActivities: Map<string, UserActivity[]>): Map<string, number> {
    const map = new Map<string, number>();
    groupedActivities
      .forEach((activities, month) =>
        map.set(month, this.countUniqueUsers(activities)));
    return map;
  }

  private toDailyUserCountMap(userActivities: UserActivity[]): Map<string, number> {
    const sortedDates = this.getUniqueSortedDates(userActivities);
    const dateToUserCountMap = new Map<string, number>();
    sortedDates.forEach(date => {
      const relevantActivities = userActivities.filter(activity => activity.date === date);
      dateToUserCountMap.set(date, this.countUniqueUsers(relevantActivities));
    });
    return dateToUserCountMap;
  }

  private countUniqueUsers(userActivities: UserActivity[]): number {
    return new Set(userActivities.map(activity => activity.userId)).size;
  }

  private getUniqueSortedDates(userActivities: UserActivity[]) {
    const sortedDates = userActivities
      .map(activity => activity.date)
      .sort((a, b) => moment(a).diff(moment(b)));
    return new Set(sortedDates);
  }

  /**
   * Sorts a map in descending order.
   */
  keyDescOrder = (a: KeyValue<string, number>, b: KeyValue<string, number>): number => {
    return a.key > b.key ? -1 : (b.key > a.key ? 1 : 0);
  }
}
